/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_ROUGHNESS, TXT_ROUGHNESSES } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addRoughnessInput,
  deleteRoughnessInput,
  getLithologiesInput,
  getRoughnessOutput,
  GET_ROUGHNESS,
  updateRoughnessInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageRoughnesssTable } from './ManageRoughnessesTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { roughness } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_ROUGHNESS,
  DELETE_ROUGHNESS,
  UPDATE_ROUGHNESS
} from '../../../api';
import { ManageRoughnessEditor } from './ManagerRoughnessEditor';

export const ManageRoughnesss = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [alterationData, setAlterationData] = useState<roughness[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedRoughness, setSelectedRoughness] = useState<roughness | undefined>(undefined);
  const [getRoughnesss, { loading, data, error, refetch }] = useLazyQuery<getRoughnessOutput>(GET_ROUGHNESS);
  const [getAllAlterations] = useLazyQuery<getRoughnessOutput>(GET_ROUGHNESS, {
    onCompleted: (data) => setAlterationData(data.roughnesses.items)
  });

  useEffect(() => {
    getRoughnesss();
    getAllAlterations();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchRoughnesss = () => {
    const variables: getLithologiesInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getRoughnesss({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchRoughnesss();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditRoughnessEditor = (roughness: roughness) => {
    setSelectedRoughness(roughness);
    setOpenEditor(true);
  };

  const openDeleteRoughnessConfirmation = (roughness: roughness) => {
    setSelectedRoughness(roughness);
    setOpenDeleteConfirmation(true);
  };

  const openAddRoughnessEditor = () => {
    setSelectedRoughness(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingRoughness = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingRoughnessError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addRoughness, addRoughnessOperation] = useMutation<roughness>(ADD_ROUGHNESS, {
    onError: (e) => handleAddingOrUpdatingRoughnessError(e),
    onCompleted: (result) => afterAddingOrUpdatingRoughness()
  });

  const [updateRoughness, updateRoughnessOperation] = useMutation<roughness>(UPDATE_ROUGHNESS, {
    onError: (e) => handleAddingOrUpdatingRoughnessError(e),
    onCompleted: (result) => afterAddingOrUpdatingRoughness()
  });

  const onEditorSave = (payload: addRoughnessInput | updateRoughnessInput) => {
    if (!selectedRoughness) {
      addRoughness({
        variables: {
          input: payload
        }
      });
    } else {
      updateRoughness({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteRoughness, deleteRoughnessOperation] = useMutation<boolean>(DELETE_ROUGHNESS, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.roughnesses.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteRoughnessInput = {
      id: selectedRoughness!.id!
    };
    deleteRoughness({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_ROUGHNESSES))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageRoughnessEditor
              roughness={selectedRoughness}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedRoughness ? addRoughnessOperation.loading :
                  updateRoughnessOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteRoughnessOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedRoughness?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-roughness-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddRoughnessEditor}>
                        {t(TXT_ADD_ROUGHNESS).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_ROUGHNESSES} data={alterationData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageRoughnesssTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditRoughnessEditor}
                    onDelete={openDeleteRoughnessConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.roughnesses.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
